import type { Filter } from '@/models/Filter';
import type { LocationDetailsOptions } from '@/utils/filters/options/getLocationDetailsOptions';
import type { BrandAndChainResponse } from '@/app/api/venue-autocomplete/route';
import { shouldVisible } from '@/utils/filters/filterHelper';
import VenueAreaFilter from '@/components/filters/VenueAreaFilter';
import CountryFilter from '@/components/filters/CountryFilter';
import dynamic from 'next/dynamic';
import CityFilter from '@/components/filters/city/CityFilter';

const AirportDistanceFilter = dynamic(() => import('@/components/filters/AirportDistanceFilter'), { ssr: false });
const MaxDistanceFilter = dynamic(() => import('@/components/filters/MaxDistanceFilter'), { ssr: false });

type Props = {
  options: LocationDetailsOptions;
  selected: Filter;
  brandCityCountry: BrandAndChainResponse | null;
  setSelected: (selections: Filter) => void;
  labels: {
    title: string;
    selectAll: string;
    clear: string;
  };
  applicableFilters: string[];
  inputNumberError: string;
  text: {
    airportDistance: string;
    maximumRadius: string;
    locationRadiusMessage: string;
  };

  testID?: string;
};

export default function LocationDetailsFilter({
  options,
  selected,
  setSelected,
  labels,
  brandCityCountry,
  applicableFilters,
  inputNumberError,
  text,
  testID
}: Props): JSX.Element {
  return (
    <div className="border-y border-neutral-20 py-3">
      <div className="py-3 text-b-xl/loose font-medium">{labels.title}</div>
      <VenueAreaFilter
        options={options}
        selected={selected}
        setSelected={setSelected}
        labels={labels}
        testID={testID}
      />
      {shouldVisible(['COUNTRY_CODE'], applicableFilters, selected) && brandCityCountry && (
        <CountryFilter
          options={brandCityCountry.countryOptions}
          selected={selected}
          setSelected={setSelected}
          testID={testID}
        />
      )}
      {brandCityCountry && (
        <CityFilter
          options={brandCityCountry.cityOptions}
          selected={selected}
          setSelected={setSelected}
          testID={testID}
        />
      )}
      <AirportDistanceFilter
        selected={selected}
        setSelected={setSelected}
        label={text.airportDistance}
        inputNumberError={inputNumberError}
        testID={testID}
      />
      <MaxDistanceFilter
        selected={selected}
        setSelected={setSelected}
        label={text.maximumRadius}
        locationRadiusMessage={text.locationRadiusMessage}
        inputNumberError={inputNumberError}
        testID={testID}
      />
    </div>
  );
}
